import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import type { AuthMeResponse, ServerFeatures } from '@acs/shared'
import { NO_FEATURES } from '@acs/shared'
import { ApiError, apiGet, apiPost } from '@/lib/api'

/** What `/api/auth/me` last said — who is signed in, and what this deployment can do. */
export type AuthState = AuthMeResponse

export const authKey = ['auth', 'me'] as const

/**
 * Who am I.
 *
 * `retry: false` matters: a 401 here is the answer, not a failure, and retrying
 * it three times would delay the login screen by seconds on every cold load.
 * `staleTime: Infinity` because the answer only changes via the two mutations
 * below, which write the cache directly.
 */
export function useAuth(): UseQueryResult<AuthState> {
  return useQuery({
    queryKey: authKey,
    queryFn: ({ signal }) => apiGet<AuthState>('/api/auth/me', undefined, signal),
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  })
}

/**
 * Deployment capabilities, as `/api/auth/me` last reported them.
 *
 * Until it has answered — or if it never does — nothing optional is on, so a
 * feature is drawn as absent rather than as a button that fails. The
 * mutations below carry the last-known block across sign-in and sign-out,
 * because a deployment fact does not change with the session.
 */
export function useFeatures(): ServerFeatures {
  const auth = useAuth()
  return auth.data?.features ?? NO_FEATURES
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { username: string; password: string }) =>
      apiPost<{ ok: true; subject: string }>('/api/auth/login', vars),
    onSuccess: (data) => {
      queryClient.setQueryData<AuthState>(authKey, (prev) => ({
        authenticated: true,
        subject: data.subject,
        // The probe before sign-in already carried the features; keep them
        // rather than flashing every optional button off until the refetch.
        features: prev?.features ?? NO_FEATURES,
      }))
      // Everything cached before sign-in was fetched without a session, so it
      // is either empty or an error. Start clean rather than showing the
      // failed state of a query that has since become answerable.
      void queryClient.invalidateQueries()
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<{ ok: true }>('/api/auth/logout'),
    onSettled: () => {
      // Clear on settle, not on success: if the request failed because the
      // cookie was already gone, the local state must still end up signed out.
      queryClient.setQueryData<AuthState>(authKey, (prev) => ({
        authenticated: false,
        subject: null,
        features: prev?.features ?? NO_FEATURES,
      }))
      queryClient.clear()
    },
  })
}

/** Human-readable reason a sign-in failed. */
export function loginErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Those credentials are not correct.'
    if (error.status === 429) return 'Too many attempts. Wait a minute and try again.'
    if (error.status >= 500) return 'The server is not responding. Try again shortly.'
    return error.message
  }
  // A network-level failure has no status — most often the API is not running.
  return 'Could not reach the server. Check that the API is running.'
}
